<template>
  <NewAutocomplete
    ref="inventoryAutocomplete"
    :label="currentInventoryTypeName ?? ''"
    :value="modelValue"
    :use-search-items="searchInventories"
    :value-resolver="valueResolver"
    :is-disabled="isDisabled"
    :hint="hint"
    placeholder="Item"
    :required="required"
    :error-messages="errorMessages"
    @selected="updateNewValue"
  />
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import NewAutocomplete from '@/components/vision/next/inputs/NewAutocomplete.vue'
import { useJobInventoryPicker } from './JobInventoryPicker'

interface InventoryPickerProps {
  modelValue?: number | null
  resourceTypeActivityId?: number | null
  disableInventorySubTypes?: number[]
  isDisabled?: boolean
  hint?: string | null
  errorMessages?: string[]
  required?: boolean
}

const props = withDefaults(defineProps<InventoryPickerProps>(), {
  modelValue: null,
  hint: null,
  required: true,
})

const emit = defineEmits<{
  (e: 'update:modelValue', id: number | null | undefined): void
}>()

const resourceTypeActivityIdRef = computed(() => props.resourceTypeActivityId ?? null)
const disableInventorySubTypesRef = computed(() => props.disableInventorySubTypes ?? [])

const {
  allInventoryTypes,
  allFilteredInventorySubTypes,
  isInitialLoading,
  searchInventories,
  valueResolver,
} = useJobInventoryPicker(resourceTypeActivityIdRef, disableInventorySubTypesRef)

const currentInventoryTypeName = computed(() => {
  if (!props.modelValue) return null

  for (const inventoryType of allInventoryTypes.value ?? []) {
    const hasSubType = inventoryType.inventorySubTypes?.some(
      (subType) => subType.id === props.modelValue,
    )

    if (hasSubType) {
      return inventoryType.name
    }
  }

  return null
})

const updateNewValue = (newVal?: number | null) => {
  emit('update:modelValue', newVal)
}

// Auto-select if only one inventory sub-type is available
watch(
  [isInitialLoading, allFilteredInventorySubTypes],
  ([loading, subTypes]) => {
    if (
      !loading &&
      subTypes &&
      subTypes.length === 1 &&
      !disableInventorySubTypesRef.value.includes(subTypes[0].id)
    ) {
      updateNewValue(subTypes[0].id ?? -1)
    }
  },
  { immediate: true },
)

const inventoryAutocomplete = ref<InstanceType<typeof NewAutocomplete> | null>(null)
defineExpose({
  focus: () => {
    inventoryAutocomplete.value?.focus()
  },
})
</script>
